/**
 * useAddIoTDevice — THE "Add IoT device" flow, shared by every surface that offers it.
 *
 * The sheet itself is <AddIoTDeviceModal>; this hook is the part that must not be written
 * twice: WHICH asset the device binds to, and what to do when the element the user picked
 * does not have one yet.
 *
 * THE RULE IT ENFORCES: a device binds to an ASSET (point_of_interests → iot_device_pois,
 * migration 043), never to a raw model element. So the bare-element entry point
 * ("📡 Create asset & add IoT") is:
 *
 *     1. create the asset  → the viewer's OWN create-asset handler, injected as
 *                            `createAssetFromElement`; this hook never re-implements it
 *                            (RVT: rvtExtractorService → RVTToPOIConverter → POI batch)
 *     2. open the sheet    → attach an existing device, or create one
 *     3. bind              → one row in iot_device_pois (+ the deprecated mirror column)
 *
 * and cancelling at step 2 deletes the asset created at step 1 — the mockup's "one undo",
 * so a user who backs out is not left with an asset they never asked for. A surface with no
 * create-asset handler (the IFC viewer, deliberately — see WebIfcViewer) simply passes none,
 * so buildElementActions never renders the entry.
 */

import { useCallback, useState } from 'react';
import { rollbackCreatedAsset } from './rollbackCreatedAsset';
import type { AddIoTDeviceTarget, AddIoTDeviceDismissal } from './AddIoTDeviceModal';

export interface UseAddIoTDeviceConfig {
  /** Site id; the flow self-gates on it (no site → the actions are never offered). */
  siteId?: string | null;
  /** Model file name shown in the sheet's context line. */
  modelName?: string | null;
  /**
   * Create an asset from a model element and RESOLVE WITH IT (the created POI row) — the
   * viewer's existing single-element creation path, not a copy of it. Omit on surfaces that
   * cannot create assets from an element.
   */
  createAssetFromElement?: (elementId: number, target?: ElementModelTarget) => Promise<any | null>;
  /** Called after any successful bind (and after an undo) so the host reloads its POIs. */
  onDataChanged?: () => void;
}

/**
 * The model an element was picked in, when the surface knows it (Autodesk, federated sites):
 * element numbers repeat across models, so the asset must be built from THIS one.
 */
export interface ElementModelTarget {
  model?: any;
  sourceFile?: string | null;
}

export interface UseAddIoTDevice {
  /** Open the sheet for an asset that already exists (element menu, Browser row ⋯). */
  openForPOI: (poi: any, element?: { idLabel?: string | null } | null) => void;
  /** Bare element: create the asset first, then open the sheet bound to it. */
  openForElement: (elementId: number, options?: { idLabel?: string | null; target?: ElementModelTarget }) => Promise<void>;
  /** Non-null while the sheet is open — render <AddIoTDeviceModal target={…} …/>. */
  target: AddIoTDeviceTarget | null;
  /** Sheet dismissal: rolls the just-created asset back when nothing was bound. */
  dismiss: (result: AddIoTDeviceDismissal) => void;
}

export function useAddIoTDevice(config: UseAddIoTDeviceConfig): UseAddIoTDevice {
  const { siteId, modelName, createAssetFromElement, onDataChanged } = config;
  const [target, setTarget] = useState<AddIoTDeviceTarget | null>(null);

  const openForPOI = useCallback((poi: any, element?: { idLabel?: string | null } | null) => {
    if (!siteId || !poi?.id) return;
    setTarget({
      siteId,
      poi,
      element: { idLabel: element?.idLabel ?? null, model: modelName ?? null },
    });
  }, [siteId, modelName]);

  const openForElement = useCallback(async (elementId: number, options?: { idLabel?: string | null; target?: ElementModelTarget }) => {
    if (!siteId || !createAssetFromElement) return;
    // The busy state belongs to the injected creation path (it already tracks the element it
    // is creating, and the info panel's button reads that) — duplicating it here would be a
    // second source of truth for one action.
    const poi = await createAssetFromElement(elementId, options?.target);
    // That path reports its own failure (toast); without a POI there is nothing to bind to,
    // and opening the sheet anyway would invite an orphan device.
    if (!poi?.id) return;
    setTarget({
      siteId,
      poi,
      element: { idLabel: options?.idLabel ?? `id ${elementId}`, model: options?.target?.sourceFile ?? modelName ?? null },
      assetWasCreated: true,
    });
  }, [siteId, modelName, createAssetFromElement]);

  const dismiss = useCallback((result: AddIoTDeviceDismissal) => {
    const dismissed = target;
    setTarget(null);
    if (!dismissed) return;

    // Undo: the asset only existed to carry a device, and no device was bound. The shared
    // helper is the one the ticket flow uses too, so both undo — and fail — the same way.
    if (!result.bound && dismissed.assetWasCreated && dismissed.poi?.id) {
      void rollbackCreatedAsset(dismissed.siteId, dismissed.poi.id, 'device')
        .finally(() => onDataChanged?.());
      return;
    }
    if (result.bound) onDataChanged?.();
  }, [target, onDataChanged]);

  return { openForPOI, openForElement, target, dismiss };
}

export default useAddIoTDevice;
